/**
 * Data model for supplier onboarding cases. Every case, from the rich Kestrel
 * case to a freshly invited supplier, is described with the same types: eight
 * steps, each with a status and content blocks (extracted fields, checks and
 * documents), plus optional rich blocks for the steps that need them.
 */
import type {
  CategoryId,
  MaterialId,
  PersonId,
  ProcurementType,
  RiskLevel,
  SupplierId,
} from '../../data/types';

export type OnboardingStepKey =
  | 'profile'
  | 'tax-banking'
  | 'financial'
  | 'legal'
  | 'quality'
  | 'insurance-esg'
  | 'agreements'
  | 'approvals';

export const STEP_ORDER: OnboardingStepKey[] = [
  'profile',
  'tax-banking',
  'financial',
  'legal',
  'quality',
  'insurance-esg',
  'agreements',
  'approvals',
];

export const STEP_LABELS: Record<OnboardingStepKey, string> = {
  profile: 'Company profile',
  'tax-banking': 'Tax and banking',
  financial: 'Financial health',
  legal: 'Legal and compliance',
  quality: 'Quality and regulatory',
  'insurance-esg': 'Insurance and sustainability',
  agreements: 'Agreements',
  approvals: 'Approvals',
};

export type StepStatus = 'done' | 'attention' | 'pending' | 'waiting' | 'na';

export const STEP_STATUS_LABEL: Record<StepStatus, string> = {
  done: 'Done',
  attention: 'Needs attention',
  pending: 'Pending',
  waiting: 'Waiting on supplier',
  na: 'Not required',
};

export type WaitingOn = 'Supplier' | 'Pillar' | 'Agent' | 'None';

/** Where an extracted value came from. */
export interface SourceRef {
  /** File name of an uploaded document, or the name of a registry or screening provider. */
  name: string;
  /** An uploaded document, a registry or screening lookup, or the supplier portal form. */
  kind: 'document' | 'lookup' | 'portal';
  /** AI extraction confidence, 0-100. */
  confidence: number;
}

export interface ExtractedField {
  label: string;
  value: string;
  source?: SourceRef;
  mono?: boolean;
  /** Visual emphasis on the value. */
  tone?: 'ok' | 'warn';
}

export interface FieldGroup {
  title?: string;
  fields: ExtractedField[];
}

export type CheckOutcome = 'pass' | 'flag' | 'pending' | 'fail';

export interface CheckItem {
  label: string;
  outcome: CheckOutcome;
  detail: string;
  /** Provider or document behind the check. */
  source?: string;
  /** Days since the check ran. Unset while it is pending. */
  checkedDaysAgo?: number;
}

export interface CheckGroup {
  title?: string;
  checks: CheckItem[];
}

export interface DocItem {
  name: string;
  state: 'received' | 'requested' | 'expected';
  /** Days since it was received or requested. */
  daysAgo?: number;
  fieldsExtracted?: number;
}

export interface TrendPoint {
  year: string;
  /** Revenue, millions of EUR. */
  revenueEurM: number;
  /** Credit score 0-100 at fiscal year end. */
  creditScore: number;
}

/** A sample unit sent for qualification testing at Pillar. */
export interface SampleUnit {
  serial: string;
  result: 'passed' | 'in_test';
  detail: string;
}

export interface AuditPlan {
  /** Days from today. */
  inDays: number;
  auditor: PersonId;
  scope: string;
}

export interface Deviation {
  id: string;
  topic: string;
  supplierAsk: string;
  standard: string;
  recommendation: string;
  /** Clause the Pillar standard comes from (`contractId:section`). */
  standardClauseId?: string;
  state: 'open' | 'countered';
}

export interface AgreementItem {
  name: string;
  detail: string;
  status: 'signed' | 'draft' | 'pending';
  contractId?: string;
}

export interface ApprovalItem {
  role: string;
  person: PersonId;
  status: 'approved' | 'pending' | 'not_started';
  /** Days since the decision. */
  decidedDaysAgo?: number;
  note: string;
}

export interface StepContent {
  summary: string;
  fieldGroups: FieldGroup[];
  checkGroups: CheckGroup[];
  documents: DocItem[];
  /** Financial health: three-year trend. */
  trend?: TrendPoint[];
  /** Quality: sample units under test and the planned on-site audit. */
  units?: SampleUnit[];
  audit?: AuditPlan;
  /** Agreements. */
  agreements?: AgreementItem[];
  deviations?: Deviation[];
  /** Approvals. */
  approvals?: ApprovalItem[];
}

export interface StepState {
  status: StepStatus;
  content: StepContent;
}

export interface AgentLogItem {
  /** Minutes before now. */
  minutesAgo: number;
  text: string;
  detail?: string;
}

export interface RiskDimension {
  label: string;
  /** 0-100, higher is riskier. Null while the dimension has not been assessed. */
  score: number | null;
}

export interface AiSummary {
  title: string;
  body: string;
  tone: 'neutral' | 'warning' | 'success';
}

export interface OnboardingContact {
  name: string;
  title: string;
  email?: string;
}

export interface OnboardingCase {
  /** 'ONB-0087' */
  id: string;
  supplierName: string;
  supplierId?: SupplierId;
  type: ProcurementType;
  categoryId: CategoryId;
  /** Plain description of what is being bought from this supplier. */
  purpose: string;
  requestedBy: PersonId;
  /** Queue label such as 'Approvals' or 'Waiting on supplier'. */
  stage: string;
  /** 0-100. */
  progress: number;
  riskLevel: RiskLevel | null;
  riskScore: number | null;
  riskDimensions: RiskDimension[];
  /** Oversight level text once assigned. */
  riskTier?: string;
  /** Oversight level the agent proposes before approval. */
  proposedRiskTier?: string;
  /** The agent's one-paragraph read of where the case stands. */
  aiSummary: AiSummary;
  openedDaysAgo: number;
  /** Set once the supplier is created in the ERP. */
  completedDaysAgo?: number;
  waitingOn: WaitingOn;
  /** What exactly is being waited for, such as 'Tax form and bank letter'. */
  waitingDetail?: string;
  contact: OnboardingContact;
  materialId?: MaterialId;
  sourcingEventId?: string;
  /** ERP supplier number, once created. */
  supplierMasterId?: string;
  /** Payment terms set on the supplier record, once created. */
  paymentTerms?: string;
  steps: Record<OnboardingStepKey, StepState>;
  agentLog: AgentLogItem[];
}

export function emptyContent(summary: string): StepContent {
  return { summary, fieldGroups: [], checkGroups: [], documents: [] };
}
